import React, { useContext, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import OfferPartnerGeneralDataContainer from './OfferPartnerGeneralDataContainer';
import OfferPartnerAddressContainer from './OfferPartnerAddressContainer';
import AddressesModal from './AddressesModal';
import OfferAddPartnersFromHierarchyContainer from './OfferAddPartnersFromHierarchyContainer';
import AddressTypesModal from './AddressTypesModal';
import OfferPartnerContactData from './OfferPartnerContactData';
import memoize from 'lodash.memoize';
import { Grid } from '@mui/material';
import { Button, Dialog, IconButton } from '@totalsoft/rocket-ui';
import { Prompt } from 'react-router-dom';
import { addPropertyPrefix } from 'utils/propertyChangeAdapters';
import { RightsContext } from 'providers/RightsContext';
import AddIcon from '@mui/icons-material/Add';
import { partnerType } from 'constants/partnerType';
import { connect } from 'react-redux';
import { getPartnerExternalVerificationStatus } from '../selectors';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ThinDivider from 'components/common/ThinDivider';
import { BoxActionsWrapper, GridActionsWrapper } from 'components/common/CommonStyles';

const addContactModePrefix = memoize(addPropertyPrefix('contactMode'));
const disabledPartnerRolesCodes = ['SHAR_HOLD', 'SHAR_HOLDL2', 'ADM', 'CONTACT'];

const emptyObject = Object.freeze({});

const OfferPartnerForm = ({
  partnerId,
  partner,
  isAssociatedPartner,
  isRealBeneficiary,
  realBeneficiaryRole,
  onPropertyChange,
  onUniqueIdBlur,
  onSave,
  onDelete,
  onDeleteDocumentPartner,
  isDirty,
  validationInfo,
  checkMandatoryFields,
  saving,
  deleting,
  onGDPRPrint,
  printingGdpr,
  t,
  documentId,
  siteId,
  addressTypes,
  onAddAddress,
  onDeleteAddress,
  defaultPartnerType,
  dictionaryAddressType,
  isFacilityFrame,
  canSearchExternally,
  handleExternalSearch,
  setIsPartnerFromModal,
  waitingForOCR,
  partnerAddresses,
  socialAddressTypeId,
  isExternalVerificationInProgress,
  loading,
  documentPartnerAllowedRoles
}) => {
  const [openRemovePartnerConfirm, setOpenRemovePartnerConfirm] = useState(false);

  const {
    readOnlyFull,
    isImage,
    clientRemoveAssociatedPartnerRead,
    financedAssetClientRead,
    offerClientResetClientData,
    offerPartnerContactDataRead,
    offerPartnerAddressesRead,
    offerPartnerContactDataWrite,
    offerPartnerAddressesWrite
  } = useContext(RightsContext);

  const handleDeleteDocumentPartner = useCallback(() => {
    setOpenRemovePartnerConfirm(false);
    onDeleteDocumentPartner();
  }, [onDeleteDocumentPartner]);

  const handleOpenRemoveConfirmation = () => {
    setOpenRemovePartnerConfirm(true);
  };

  const handleCloseRemoveConfirmation = () => {
    setOpenRemovePartnerConfirm(false);
  };

  const [showAddressTypesModal, setShowAddressTypesModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const closeAddressTypesModal = () => setShowAddressTypesModal(false);
  const [anchor, setAnchor] = useState(null);

  const openAddressTypesModal = event => {
    setShowAddressTypesModal(true);
    setAnchor(event.currentTarget);
  };

  const closeAddressesModal = () => setShowAddressModal(false);

  const getPartnerAddressTypes = partnerTypeCode => {
    if (!partnerTypeCode) return [];
    let partnerAddressTypes = JSON.parse(addressTypes)[partnerTypeCode ?? partnerType.PF];
    if (!partnerAddressTypes) {
      if (!defaultPartnerType) partnerAddressTypes = JSON.parse(addressTypes)[partnerType.PF];
      else partnerAddressTypes = JSON.parse(addressTypes)[defaultPartnerType.partnerTypeCode];
    }

    return partnerAddressTypes || [];
  };

  const partnerAddressTypesWithoutDefault = getPartnerAddressTypes(partner.partnerType?.partnerTypeCode).filter(addr => !addr.isDefault);

  const addresses = dictionaryAddressType.filter(addr => partnerAddressTypesWithoutDefault.find(pat => pat.code === addr.code));

  const [showHierarchyPartnersModal, setShowHierarchyPartnersModal] = useState(false);
  const addHierarchyPartnersModal = () => setShowHierarchyPartnersModal(true);
  const closeHierarchyPartnersModal = () => setShowHierarchyPartnersModal(false);

  const isAssociatedDocumentPartner =
    partner.partnerRoleCode && !documentPartnerAllowedRoles.split(';').includes(partner.partnerRoleCode) && isAssociatedPartner;

  const checkAgreement = partner.partnerType?.typeCheck?.isPF
    ? isAssociatedDocumentPartner
      ? true
      : partner.agreesWithPersonalDataUse
    : true;
  const partnerRoleDisabled = isAssociatedDocumentPartner && disabledPartnerRolesCodes.includes(partner.partnerRoleCode);

  const isDocumentPartnerAllowedRoles = partner.partnerRoleCode && documentPartnerAllowedRoles.split(';').includes(partner.partnerRoleCode);

  const handleAddressPropertyChange = useMemo(() => memoize(index => addPropertyPrefix(index, onPropertyChange)), [onPropertyChange]);

  const saveDisabled = useMemo(
    () => !isDirty || readOnlyFull || saving || !validationInfo.isValid || waitingForOCR || isExternalVerificationInProgress || loading,
    [isDirty, readOnlyFull, saving, validationInfo.isValid, waitingForOCR, isExternalVerificationInProgress, loading]
  );
  const partnerRole = useMemo(
    () =>
      (partner.partnerRoleId && {
        partnerRoleId: partner.partnerRoleId,
        partnerRoleCode: partner.partnerRoleCode,
        name: partner.partnerRoleName
      }) ||
      (isRealBeneficiary
        ? realBeneficiaryRole
        : {
            partnerRoleId: partner.partnerRoleId,
            partnerRoleCode: partner.partnerRoleCode,
            name: partner.partnerRoleName
          }),
    [partner?.partnerRoleId, partner?.partnerRoleCode, partner?.partnerRoleName, isRealBeneficiary, realBeneficiaryRole]
  );

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container>
        <OfferPartnerGeneralDataContainer
          partnerId={partnerId}
          partnerGeneralData={partner.generalData || emptyObject}
          partnerType={partner.partnerType}
          documentId={documentId}
          siteId={siteId}
          partnerRole={partnerRole}
          onPropertyChange={onPropertyChange}
          onUniqueIdBlur={onUniqueIdBlur}
          partnerRoleIdValidationInfo={validationInfo.partnerRoleId}
          validationInfo={validationInfo.generalData || emptyObject}
          checkMandatoryFields={checkMandatoryFields}
          isAssociatedPartner={isAssociatedPartner}
          agreesWithPersonalDataUse={partner.agreesWithPersonalDataUse}
          agreesWithPersonalDataUseReadOnly={partner.agreesWithPersonalDataUseReadOnly || false}
          isFacilityFrame={isFacilityFrame}
          setIsPartnerFromModal={setIsPartnerFromModal}
          canSearchExternally={canSearchExternally}
          handleExternalSearch={handleExternalSearch}
          waitingForOCR={waitingForOCR}
          isAssociatedDocumentPartner={isAssociatedDocumentPartner}
          partnerRoleDisabled={partnerRoleDisabled}
        />
        {offerPartnerContactDataRead && (
          <Grid item xs={12}>
            <ThinDivider orientation='horizontal' />
          </Grid>
        )}
        {offerPartnerContactDataRead && (
          <Grid item xs={12} mb={4}>
            <OfferPartnerContactData
              contactMode={partner.contactMode || emptyObject}
              validationInfo={validationInfo.contactMode || emptyObject}
              partnerTypeCode={partner.partnerType?.partnerTypeCode}
              disabled={Boolean(
                readOnlyFull ||
                  !offerPartnerContactDataWrite ||
                  waitingForOCR ||
                  (partner.partnerType?.typeCheck?.isPF && !partner.agreesWithPersonalDataUse && !isAssociatedDocumentPartner)
              )}
              onPropertyChange={addContactModePrefix(onPropertyChange)}
            />
          </Grid>
        )}
        {offerPartnerAddressesRead && (
          <Grid item xs={12}>
            <ThinDivider orientation='horizontal' />
          </Grid>
        )}
        {offerPartnerAddressesRead &&
          partner.addresses?.length > 0 &&
          partner.partnerType &&
          partner.addresses.map((address, index) => {
            return (
              <Grid item xs={12} key={index} mb={2}>
                <OfferPartnerAddressContainer
                  index={index}
                  onDeleteAddress={onDeleteAddress}
                  onPropertyChange={handleAddressPropertyChange(index)}
                  address={address || emptyObject}
                  partnerTypeCode={partner.partnerType?.partnerTypeCode}
                  partnerTypeId={partner.partnerType?.partnerTypeId}
                  disabled={readOnlyFull || !offerPartnerAddressesWrite || waitingForOCR || !checkAgreement}
                  checkMandatoryFields={checkMandatoryFields}
                  validationInfo={(validationInfo.addresses && validationInfo.addresses[index]) || emptyObject}
                />
                <Grid item xs={12} mt={2}>
                  <ThinDivider />
                </Grid>
              </Grid>
            );
          })}
        {checkAgreement && (
          <>
            {showAddressTypesModal && (
              <AddressTypesModal
                documentId={documentId}
                siteId={siteId}
                partner={partner}
                addAddress={onAddAddress}
                closeAddressTypesModal={closeAddressTypesModal}
                anchor={anchor}
                addressTypes={partnerAddressTypesWithoutDefault}
                addresses={addresses}
              />
            )}
          </>
        )}
        <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
          {!isImage && (
            <>
              <GridActionsWrapper>
                <Button id={'offerPartnerFormCard_saveBtn'} onClick={onSave} disabled={saveDisabled}>
                  {saving ? t('Saving') : t('Save')}
                </Button>
                {!isAssociatedDocumentPartner &&
                  isDocumentPartnerAllowedRoles &&
                  !!partner.partnerId &&
                  clientRemoveAssociatedPartnerRead && (
                    <Button id={'offerPartnerFormCard_deleteBtn'} onClick={onDelete} disabled={deleting || waitingForOCR || readOnlyFull}>
                      {deleting ? t('OfferEdit.Client.Deleting') : t('OfferEdit.Client.Delete')}
                    </Button>
                  )}
                {partner.partnerType?.typeCheck?.isPF && (
                  <Button
                    id={'offerPartnerFormCard_printGdprBtn'}
                    onClick={onGDPRPrint}
                    disabled={
                      printingGdpr ||
                      readOnlyFull ||
                      waitingForOCR ||
                      (partner.partnerType?.typeCheck?.isPF && !partner.agreesWithPersonalDataUse && !isAssociatedDocumentPartner) ||
                      !partner.masterPartnerId ||
                      partner.masterPartnerId == null
                    }
                  >
                    {printingGdpr ? t('PrintingGDPR') : t('PrintGDPR')}
                  </Button>
                )}
                {!isAssociatedPartner && financedAssetClientRead && offerClientResetClientData && (
                  <Button
                    id={'clientDataCard_resetDataBtn'}
                    disabled={
                      deleting ||
                      (partner.partnerType?.typeCheck?.isPF && !partner.agreesWithPersonalDataUse && !isAssociatedDocumentPartner) ||
                      isAssociatedPartner ||
                      readOnlyFull ||
                      waitingForOCR
                    }
                    onClick={handleOpenRemoveConfirmation}
                  >
                    {deleting ? t('OfferEdit.Client.ResettingClientData') : t('OfferEdit.Client.ResetClientData')}
                  </Button>
                )}
              </GridActionsWrapper>
              <GridActionsWrapper>
                {checkAgreement && offerPartnerAddressesRead && (
                  <IconButton
                    id={'offerPartnerFormCard_addAddressBtn'}
                    color='primary'
                    variant='text'
                    size='small'
                    tooltip={t('OfferEdit.Client.AddAddress')}
                    onClick={openAddressTypesModal}
                    disabled={
                      readOnlyFull ||
                      !offerPartnerAddressesWrite ||
                      waitingForOCR ||
                      (partner.partnerType?.typeCheck?.isPF && !partner.agreesWithPersonalDataUse && !isAssociatedDocumentPartner)
                    }
                  >
                    <AddIcon />
                  </IconButton>
                )}
                {offerPartnerAddressesRead && checkAgreement && partnerAddresses?.length > 0 && (
                  <IconButton
                    id={'offerPartnerFormCard_addAddressBtn'}
                    color='primary'
                    variant='text'
                    size='small'
                    tooltip={t('OfferEdit.Client.ExistingAddresses', {
                      data: { noAddresses: partnerAddresses?.length }
                    })}
                    disabled={isDirty || saving}
                    onClick={() => {
                      setShowAddressModal(true);
                    }}
                  >
                    <LocationOnOutlinedIcon />
                  </IconButton>
                )}

                {isFacilityFrame && !isAssociatedPartner && (
                  <IconButton
                    id={'offerPartnerFormCard_addHierarchyPartners'}
                    color='primary'
                    variant='text'
                    size='small'
                    tooltip={t('OfferEdit.Client.AddHierarchyPartners')}
                    onClick={addHierarchyPartnersModal}
                    disabled={
                      isDirty ||
                      readOnlyFull ||
                      waitingForOCR ||
                      partner.partnerType?.typeCheck?.isPF ||
                      !partner.generalData.partnerGroupId
                    }
                  >
                    <PersonAddIcon />
                  </IconButton>
                )}
              </GridActionsWrapper>
              {checkAgreement && (
                <>
                  {showAddressModal && (
                    <AddressesModal
                      partner={partner}
                      documentId={documentId}
                      siteId={siteId}
                      partnerAddresses={partnerAddresses}
                      socialAddressTypeId={socialAddressTypeId}
                      closeAddressesModal={closeAddressesModal}
                      addressTypes={dictionaryAddressType}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
      {isFacilityFrame && !isAssociatedPartner && (
        <Grid item xs={12}>
          <OfferAddPartnersFromHierarchyContainer
            showModal={showHierarchyPartnersModal}
            onCloseHierarchyPartnersModal={closeHierarchyPartnersModal}
            partnerGroupId={partner.generalData.partnerGroupId}
            documentId={documentId}
            siteId={siteId}
          />
        </Grid>
      )}
      {openRemovePartnerConfirm && (
        <Dialog
          open={openRemovePartnerConfirm}
          onClose={handleCloseRemoveConfirmation}
          aria-labelledby='client-title'
          aria-describedby='client-description'
          title={t('OfferEdit.Client.ResetDataDialog.Title')}
          content={t('OfferEdit.Client.ResetDataDialog.Description')}
          actions={
            <BoxActionsWrapper>
              <Button id={'clientCard_resetDataAcceptBtn'} autoFocus onClick={handleDeleteDocumentPartner}>
                {t('OfferEdit.Client.ResetDataDialog.AcceptButton')}
              </Button>
              <Button id={'clientCard_resetDataRejectBtn'} onClick={handleCloseRemoveConfirmation}>
                {t('OfferEdit.Client.ResetDataDialog.RejectButton')}
              </Button>
            </BoxActionsWrapper>
          }
        />
      )}
    </>
  );
};

OfferPartnerForm.propTypes = {
  partnerId: PropTypes.number.isRequired,
  partner: PropTypes.object.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  isAssociatedPartner: PropTypes.bool,
  isRealBeneficiary: PropTypes.bool,
  realBeneficiaryRole: PropTypes.object,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDeleteDocumentPartner: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  checkMandatoryFields: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  deleting: PropTypes.bool.isRequired,
  onGDPRPrint: PropTypes.func.isRequired,
  printingGdpr: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  addressTypes: PropTypes.string.isRequired,
  onAddAddress: PropTypes.func.isRequired,
  onDeleteAddress: PropTypes.func.isRequired,
  defaultPartnerType: PropTypes.object,
  dictionaryAddressType: PropTypes.array.isRequired,
  isFacilityFrame: PropTypes.bool,
  canSearchExternally: PropTypes.object.isRequired,
  handleExternalSearch: PropTypes.func.isRequired,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  waitingForOCR: PropTypes.bool.isRequired,
  socialAddressTypeId: PropTypes.number.isRequired,
  partnerAddresses: PropTypes.array,
  isExternalVerificationInProgress: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  documentPartnerAllowedRoles: PropTypes.string.isRequired
};
const mapStateToProps = (state, ownProps) => {
  return {
    isExternalVerificationInProgress: getPartnerExternalVerificationStatus(state, ownProps),
    documentPartnerAllowedRoles: state.params.items.documentPartnerAllowedRoles
  };
};

export default compose(connect(mapStateToProps), withTranslation())(OfferPartnerForm);
